import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface GradeOption {
  value: string;
  label: string;
}

interface AgeSideSectionProps<TFormValues extends Record<string, any>> {
  form: UseFormReturn<TFormValues>;
  isMin: boolean;
  ageYearsName: keyof TFormValues & string;
  gradeKeyName: keyof TFormValues & string;
  noteName: keyof TFormValues & string;
  showGradeAsPrimaryName: keyof TFormValues & string;
  gradeOptions: GradeOption[];
}

export const AgeSideSection = <TFormValues extends Record<string, any>>({
  form,
  isMin,
  ageYearsName,
  gradeKeyName,
  noteName,
  showGradeAsPrimaryName,
  gradeOptions
}: AgeSideSectionProps<TFormValues>) => {
  const showGradeAsPrimary = useWatch({ control: form.control, name: showGradeAsPrimaryName as any });
  const showAgeFirst = !showGradeAsPrimary;

  const renderAgeField = () => (
    <FormField
      control={form.control}
      name={ageYearsName as any}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">{isMin ? 'Min age, in years' : 'Max age, in years'}</FormLabel>
          <FormControl>
            <Input
              type="number"
              min={0}
              max={20}
              step="0.1"
              value={field.value ?? ''}
              onChange={e => {
                const newValue = e.target.value !== '' ? Number(e.target.value) : null;
                field.onChange(newValue);
              }}
              placeholder="Enter age"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderGradeField = () => (
    <FormField
      control={form.control}
      name={gradeKeyName as any}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">{isMin ? 'Min grade (optional if using years old)' : 'Max grade (optional if using years old)'}</FormLabel>
          <Select
            onValueChange={value => {
              field.onChange(value === 'none' ? '' : value);
            }}
            value={field.value && field.value !== '' ? field.value : 'none'}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {gradeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <div className={isMin ? 'space-y-4 border-r pr-4 pl-1' : 'space-y-4 pr-1'}>
      <div className="flex items-center gap-2 mb-4">
        <FormLabel className="text-left block whitespace-nowrap">
          {isMin ? 'Show min age based on:' : 'Show max age based on:'}
        </FormLabel>
        <FormField
          control={form.control}
          name={showGradeAsPrimaryName as any}
          render={({ field }) => (
            <Select
              onValueChange={value => {
                field.onChange(value === 'true');
              }}
              value={field.value ? 'true' : 'false'}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">Years old</SelectItem>
                <SelectItem value="true">Grade</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showAgeFirst ? (
        <>
          {renderAgeField()}
          {renderGradeField()}
        </>
      ) : (
        <>
          {renderGradeField()}
          {renderAgeField()}
        </>
      )}

      <FormField
        control={form.control}
        name={noteName as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-left block">{isMin ? 'Min age footnote (optional)' : 'Max age footnote (optional)'}</FormLabel>
            <FormControl>
              <Input
                value={field.value ?? ''}
                onChange={e => {
                  field.onChange(e.target.value);
                }}
                placeholder=""
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};


